import { useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// Buyer self-report: possession taken or not, and — if not — why.
export function PossessionVoiceDialog({ open, onOpenChange, projectId, issues, initial, onSaved }) {
  const [status, setStatus] = useState(initial?.status || 'not_taken');
  const [wing, setWing] = useState(initial?.wing || '');
  const [picked, setPicked] = useState(initial?.issues || []);
  const [details, setDetails] = useState(initial?.details || '');
  const [saving, setSaving] = useState(false);

  const toggle = (code) => setPicked((p) => (p.includes(code) ? p.filter((x) => x !== code) : [...p, code]));

  const save = async () => {
    if (status === 'not_taken' && !picked.length) return toast.error('Pick at least one reason');
    setSaving(true);
    try {
      const r = await axios.post(`${API}/api/possession/${projectId}/report`, { status, wing, issues: picked, details });
      toast.success('Thank you — your voice is now counted');
      onSaved?.(r.data);
      onOpenChange(false);
    } catch { toast.error('Could not save. Please sign in and try again.'); }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90dvh] overflow-y-auto" data-testid="possession-voice-dialog">
        <DialogHeader>
          <DialogTitle>Your possession status</DialogTitle>
          <DialogDescription>Counted privately. Only totals are shown, never names.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-2">
            {[['not_taken', 'Not yet taken'], ['taken', 'Taken possession']].map(([v, l]) => (
              <Button key={v} type="button" variant={status === v ? 'default' : 'outline'} onClick={() => setStatus(v)}
                data-testid={`possession-status-${v}`}>{l}</Button>
            ))}
          </div>
          <div className="space-y-1">
            <Label htmlFor="pv-wing">Wing / building (optional)</Label>
            <Input id="pv-wing" value={wing} onChange={(e) => setWing(e.target.value)} placeholder="e.g. Wing 3" data-testid="possession-wing-input" />
          </div>
          {status === 'not_taken' && (
            <div className="space-y-2">
              <Label>What is holding you back? (pick all that apply)</Label>
              {issues.map((i) => (
                <label key={i.code} className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox checked={picked.includes(i.code)} onCheckedChange={() => toggle(i.code)} data-testid={`possession-issue-${i.code}`} />
                  {i.label}
                </label>
              ))}
            </div>
          )}
          <div className="space-y-1">
            <Label htmlFor="pv-details">Details (optional)</Label>
            <Textarea id="pv-details" rows={3} value={details} onChange={(e) => setDetails(e.target.value)}
              placeholder="Defects observed, pending works, demands raised…" data-testid="possession-details-input" />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={saving} data-testid="possession-voice-submit">{saving ? 'Saving…' : 'Count me in'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Admin: possession figures derived from RERA sales + corpus tendered.
export function PossessionStatsDialog({ open, onOpenChange, projectId, stats, onSaved }) {
  const [f, setF] = useState({
    units_sold: stats?.units_sold ?? '', possession_taken: stats?.possession_taken ?? '',
    basis_note: stats?.basis_note || '', wings_pending_oc: stats?.wings_pending_oc || '',
  });
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const save = async () => {
    try {
      const body = { ...f, units_sold: f.units_sold === '' ? null : Number(f.units_sold),
        possession_taken: f.possession_taken === '' ? null : Number(f.possession_taken) };
      await axios.put(`${API}/api/possession/admin/${projectId}/stats`, body);
      toast.success('Possession figures updated');
      onSaved?.();
      onOpenChange(false);
    } catch { toast.error('Could not save'); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="possession-stats-dialog">
        <DialogHeader>
          <DialogTitle>Possession figures</DialogTitle>
          <DialogDescription>Flats sold (RERA / promoter records) minus flats for which corpus was tendered or possession handed over.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1"><Label>Flats sold</Label><Input type="number" value={f.units_sold} onChange={set('units_sold')} data-testid="stats-units-sold" /></div>
            <div className="space-y-1"><Label>Possession taken</Label><Input type="number" value={f.possession_taken} onChange={set('possession_taken')} data-testid="stats-possession-taken" /></div>
          </div>
          <div className="space-y-1"><Label>Basis of figures</Label><Input value={f.basis_note} onChange={set('basis_note')} placeholder="e.g. corpus tendered for 412 flats" data-testid="stats-basis-note" /></div>
          <div className="space-y-1"><Label>Wings without OC / full OC</Label><Input value={f.wings_pending_oc} onChange={set('wings_pending_oc')} placeholder="e.g. Wing 5 — part OC only" data-testid="stats-wings-pending-oc" /></div>
        </div>
        <DialogFooter><Button onClick={save} data-testid="possession-stats-save">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
